import { useMemo } from 'react';
import { sameAccount } from '../../../utils/accountIds';
import { Account, InventoryAsset } from '../../../types';
import { belongsToAccount, LocationScope } from './serviceRequestModel';

interface LocationScopeInput {
  location?: LocationScope;
  selectedAccount: Account | null;
  accountSelectorShown: boolean;
  existingInventory: InventoryAsset[];
}

/**
 * The inventory a request may draw on: the form's account's records, and among them the ones at the location it
 * was opened from, for as long as the form stays on that location's account.
 */
export function useLocationScope({
  location,
  selectedAccount,
  accountSelectorShown,
  existingInventory
}: LocationScopeInput) {
  // Opened from a location, the form stays on that location's account even for a role without the
  // selector, so another header account's locations and assets cannot ride under it.
  const scopingAccount = (accountSelectorShown || location !== undefined) && selectedAccount;
  const scopedInventory = useMemo(
    () =>
      scopingAccount ? existingInventory.filter((asset) => belongsToAccount(asset, scopingAccount)) : existingInventory,
    [existingInventory, scopingAccount]
  );

  // The location scope holds only while the form is on the location's account; a multi-account
  // customer who switches account gets the whole account back.
  const activeLocation =
    location && (!selectedAccount || sameAccount(selectedAccount.id, location.accountId)) ? location : undefined;
  const locationRecordIds = useMemo(
    () => new Set(activeLocation?.inventory.map((record) => record.id)),
    [activeLocation]
  );
  const installedHere = useMemo(
    () =>
      (activeLocation?.installed ?? []).filter((record) => !scopingAccount || belongsToAccount(record, scopingAccount)),
    [activeLocation, scopingAccount]
  );

  // Managed products drive the Type of Service options for Move/Disconnect/Replace/Change,
  // so they follow the account-scoped inventory, narrowed to the location the form was opened from.
  const locationInventory = useMemo(() => {
    if (!activeLocation) return scopedInventory;
    const { key } = activeLocation;
    return scopedInventory.filter(
      (asset) =>
        locationRecordIds.has(asset.id) ||
        asset.locationId === key ||
        asset.locationAId === key ||
        asset.locationZId === key
    );
  }, [scopedInventory, activeLocation, locationRecordIds]);

  return { scopedInventory, installedHere, locationInventory };
}
